/**
 * Starter Stepper — wraps Mantine's `<Stepper>` with the Vintage design tokens so
 * a multi-step wizard shows its progress (e.g. the loan-data upload flow's
 * PII Preflight → Column Mapping → Validation stages) with clear current /
 * completed / upcoming states.
 *
 * Re-skins automatically with the token system: the active step circle uses the
 * brand ink (`--color-primary`), the completed check uses the gilt accent
 * (`--color-accent`), and the connecting track uses a warm-neutral border — no
 * hardcoded colors. Compound parts (`Step`, `Completed`) are re-exposed for
 * parity, matching the `Tabs` wrapper convention.
 *
 * Generic and reusable: any wizard can use it. On narrow viewports pass
 * `size="sm"` (and Mantine's `iconSize`) for a compact rail.
 */
import {
  Stepper as MantineStepper,
  type StepperProps as MantineStepperProps,
} from '@mantine/core';
import type { ReactElement } from 'react';
import styles from './Stepper.module.css';

export interface StepperProps extends MantineStepperProps {}

function StepperRoot({ classNames, ...rest }: StepperProps): ReactElement {
  const existing = (classNames ?? {}) as {
    root?: string;
    step?: string;
    stepIcon?: string;
    stepCompletedIcon?: string;
    stepLabel?: string;
    stepDescription?: string;
    separator?: string;
    separatorActive?: string;
  };
  const mergedClassNames = {
    ...existing,
    root: [styles.root, existing.root].filter(Boolean).join(' '),
    step: [styles.step, existing.step].filter(Boolean).join(' '),
    stepIcon: [styles.stepIcon, existing.stepIcon].filter(Boolean).join(' '),
    stepCompletedIcon: [styles.stepCompletedIcon, existing.stepCompletedIcon]
      .filter(Boolean)
      .join(' '),
    stepLabel: [styles.stepLabel, existing.stepLabel].filter(Boolean).join(' '),
    stepDescription: [styles.stepDescription, existing.stepDescription]
      .filter(Boolean)
      .join(' '),
    separator: [styles.separator, existing.separator].filter(Boolean).join(' '),
    separatorActive: [styles.separatorActive, existing.separatorActive]
      .filter(Boolean)
      .join(' '),
  };

  return <MantineStepper classNames={mergedClassNames} {...rest} />;
}

export const Stepper = Object.assign(StepperRoot, {
  Step: MantineStepper.Step,
  Completed: MantineStepper.Completed,
});
